import { useMemo } from 'react';

import { useQueryClient } from '@tanstack/react-query';

import {
  ChecklistDtoStatus,
  ChecklistItemDto,
  ChecklistItemDtoStatus,
  useCreateChecklist,
  useListChecklists,
  usePatchChecklist,
  useUpdateChecklist
} from '@/api/generated';

export const useCheckList = ({ enabled }: { enabled?: boolean } = {}) => {
  const queryClient = useQueryClient();
  const { data: userCheckLists } = useListChecklists({
    query: {
      enabled: enabled,
      queryKey: ['listChecklists']
    }
  });
  const checkList = userCheckLists?.items?.[0];
  const onSuccess = () => {
    queryClient.invalidateQueries({ queryKey: ['listChecklists'] });
  };

  const { mutate: createCheckList } = useCreateChecklist({
    mutation: {
      onSuccess: () => onSuccess()
    }
  });
  const { mutate: updateCheckList } = useUpdateChecklist({
    mutation: {
      onSuccess: () => onSuccess()
    }
  });
  const { mutate: patchChecklist } = usePatchChecklist({
    mutation: {
      onSuccess: () => onSuccess()
    }
  });

  const isItemCompleted = (title: string) =>
    checkList?.items?.find(i => i.title === title)?.status === ChecklistItemDtoStatus.completed;

  const updateCheckListItems = (item: Required<ChecklistItemDto>) => {
    if (!checkList || !checkList.items?.length) return;
    if (isItemCompleted(item.title)) return;
    const checkListForUpdate = [...checkList.items];
    const itemIndexForUpdate = checkListForUpdate.findIndex(i => i.title === item.title);
    checkListForUpdate[itemIndexForUpdate] = { ...item };
    updateCheckList({
      id: checkList.id || '',
      data: {
        items: checkListForUpdate
      }
    });
  };

  const isListCompleted = useMemo(() => {
    if (!checkList || !checkList.items?.length) return false;
    if (checkList.status === ChecklistDtoStatus.completed) return true;
    return checkList.items.every(i => i.status === ChecklistItemDtoStatus.completed);
  }, [checkList]);

  const progress = useMemo(() => {
    if (!checkList?.items?.length) return 0;
    const completed = checkList.items.filter(
      item => item.status === ChecklistItemDtoStatus.completed
    );
    return Math.round((completed?.length / checkList?.items?.length) * 100);
  }, [checkList]);

  return {
    userCheckList: checkList,
    hideList: checkList?.status === ChecklistDtoStatus.dismissed || !checkList || progress === 100,
    isListCompleted,
    patchChecklist,
    updateCheckList: updateCheckListItems,
    createCheckList,
    isItemCompleted,
    progress
  };
};
